import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { AdminButton } from '@/components/admin/button';
import { Card } from '@/components/admin/card';
import { PageHead } from '@/components/admin/page-head';
import { Tag } from '@/components/admin/tag';
import { api, unwrap } from '@/lib/api';

/**
 * P19 §11 — Administration des campagnes njangi : liste, conversion
 * verrouillé→payé (LA métrique de survie, §2.1), détail membres/journal,
 * actions prolonger / verrouiller / annuler.
 */

type CampaignRow = {
  id: string;
  code: string;
  title: string;
  productTitle: string;
  status: string;
  visibility: string;
  thresholdMode: string;
  currentThreshold: number;
  targetThreshold: number;
  createdByType: string;
  closesAt: string;
  paymentDueAt: string | null;
  lockedUnitPrice: number | null;
  membersCount: number;
  paid: number;
  dropped: number;
  conversion: number | null;
};

type CampaignDetail = {
  campaign: CampaignRow & { code: string };
  members: Array<{
    id: string;
    name: string;
    quantity: number;
    status: string;
    isInitiator: boolean;
    orderId: string | null;
    joinedAt: string;
  }>;
  journal: Array<{ type: string; actorType: string; createdAt: string }>;
};

type Stats = {
  campaignsTotal: number;
  campaignsFulfilled: number;
  campaignsExpired: number;
  membersPaid: number;
  membersDropped: number;
  lockToPaidConversion: number | null;
  averageNjangiOrder: number | null;
};

const STATUS_TAG: Record<string, { label: string; cls: 'success' | 'warning' | 'neutral' | 'danger' }> = {
  OPEN: { label: 'Ouverte', cls: 'success' },
  LOCKED: { label: 'Verrouillée', cls: 'warning' },
  FULFILLED: { label: 'Aboutie', cls: 'success' },
  EXPIRED: { label: 'Expirée', cls: 'neutral' },
  CANCELLED: { label: 'Annulée', cls: 'danger' },
};

const fmtF = (n: number) => `${n.toLocaleString('fr-FR')} F`;

/** Vocabulaire technique → français, comme partout ailleurs dans l'admin. */
const MEMBER_STATUS_FR: Record<string, string> = {
  JOINED: 'Engagé',
  AWAITING_PAYMENT: 'À payer',
  PAID: 'Payé ✓',
  DROPPED: 'Défaillant',
  LEFT: 'Parti',
};

const EVENT_FR: Record<string, string> = {
  'campaign.created': 'Campagne créée',
  'member.joined': 'Adhésion',
  'member.updated': 'Part ajustée',
  'member.left': 'Départ',
  'tier.reached': 'Palier débloqué',
  'campaign.locked': 'Verrouillage',
  'member.paid': 'Part payée',
  'member.dropped': 'Défection',
  'campaign.fulfilled': 'Campagne aboutie',
  'campaign.expired': 'Campagne expirée',
  'campaign.cancelled': 'Campagne annulée',
  'reminder.deadline': 'Relance J-1 envoyée',
  'reminder.payment.6h': 'Relance paiement (6 h)',
  'reminder.payment.20h': 'Relance paiement (20 h)',
};

const ACTOR_FR: Record<string, string> = {
  customer: 'client',
  admin: 'admin',
  system: 'système',
};

export function AdminNjangiPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>('');
  const [openId, setOpenId] = useState<string | null>(null);

  const stats = useQuery({
    queryKey: ['njangi-stats'],
    queryFn: () => unwrap<Stats>(api.get('/admin/njangi/stats')),
  });
  const list = useQuery({
    queryKey: ['njangi-campaigns', status],
    queryFn: () =>
      unwrap<{ items: CampaignRow[]; total: number }>(
        api.get('/admin/njangi/campaigns', { params: status ? { status } : {} }),
      ),
  });
  const detail = useQuery({
    queryKey: ['njangi-campaign', openId],
    enabled: !!openId,
    queryFn: () => unwrap<CampaignDetail>(api.get(`/admin/njangi/campaigns/${openId}`)),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['njangi-campaigns'] });
    void qc.invalidateQueries({ queryKey: ['njangi-campaign'] });
    void qc.invalidateQueries({ queryKey: ['njangi-stats'] });
  };
  const patch = useMutation({
    mutationFn: (p: { id: string; body: Record<string, unknown> }) =>
      unwrap(api.patch(`/admin/njangi/campaigns/${p.id}`, p.body)),
    onSuccess: invalidate,
  });
  const lock = useMutation({
    mutationFn: (id: string) => unwrap(api.post(`/admin/njangi/campaigns/${id}/lock`)),
    onSuccess: invalidate,
  });

  const s = stats.data;

  return (
    <div>
      <PageHead
        title="Njangi · achat groupé"
        sub="Campagnes, conversion verrouillé → payé, modération."
      />

      {/* Tableau de bord — la conversion est la métrique de survie (§2.1). */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
        {[
          ['Campagnes', s?.campaignsTotal ?? '—'],
          ['Abouties', s?.campaignsFulfilled ?? '—'],
          ['Expirées', s?.campaignsExpired ?? '—'],
          [
            'Conversion verrouillé → payé',
            s?.lockToPaidConversion != null ? `${Math.round(s.lockToPaidConversion * 100)} %` : '—',
          ],
          ['Défections', s?.membersDropped ?? '—'],
          ['Panier moyen njangi', s?.averageNjangiOrder != null ? fmtF(s.averageNjangiOrder) : '—'],
        ].map(([label, value]) => (
          <Card key={String(label)}>
            <div style={{ padding: 14 }}>
              <div className="eyebrow">{label}</div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{value}</div>
            </div>
          </Card>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {['', 'OPEN', 'LOCKED', 'FULFILLED', 'EXPIRED', 'CANCELLED'].map((st) => (
          <button
            key={st || 'all'}
            type="button"
            className={`chip-filter${status === st ? ' on' : ''}`}
            onClick={() => setStatus(st)}
          >
            {st ? (STATUS_TAG[st]?.label ?? st) : 'Toutes'}
          </button>
        ))}
      </div>

      <Card>
        <table className="table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Campagne</th>
              <th>Statut</th>
              <th className="right">Seuil</th>
              <th className="right">Payés / Défauts</th>
              <th className="right">Conversion</th>
              <th>Échéance</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(list.data?.items ?? []).map((c) => (
              <Fragment key={c.id}>
                <tr
                  onClick={() => setOpenId(openId === c.id ? null : c.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ fontFamily: 'monospace' }}>{c.code}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{c.title}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {c.productTitle} · {c.createdByType === 'admin' ? 'Akwaba' : 'client'} ·{' '}
                      {c.visibility}
                    </div>
                  </td>
                  <td>
                    <Tag variant={STATUS_TAG[c.status]?.cls ?? 'neutral'}>
                      {STATUS_TAG[c.status]?.label ?? c.status}
                    </Tag>
                  </td>
                  <td className="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {c.currentThreshold}/{c.targetThreshold}
                  </td>
                  <td className="right">
                    {c.paid} / {c.dropped}
                  </td>
                  <td className="right">
                    {c.conversion != null ? `${Math.round(c.conversion * 100)} %` : '—'}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {new Date(c.paymentDueAt ?? c.closesAt).toLocaleString('fr-FR', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="right">
                    {c.status === 'OPEN' && (
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        <AdminButton
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            patch.mutate({ id: c.id, body: { extendDays: 3 } });
                          }}
                        >
                          +3 j
                        </AdminButton>
                        <AdminButton
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            lock.mutate(c.id);
                          }}
                        >
                          Verrouiller
                        </AdminButton>
                      </span>
                    )}
                    {['OPEN', 'LOCKED'].includes(c.status) && (
                      <AdminButton
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Annuler la campagne ${c.code} ?`)) {
                            patch.mutate({ id: c.id, body: { cancel: true } });
                          }
                        }}
                      >
                        <span style={{ color: 'var(--a-danger)' }}>Annuler</span>
                      </AdminButton>
                    )}
                  </td>
                </tr>
                {openId === c.id && detail.data && (
                  <tr>
                    <td colSpan={8} style={{ background: 'var(--a-bg)' }}>
                      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: '1fr 1fr', padding: 12 }}>
                        <div>
                          <div className="eyebrow" style={{ marginBottom: 6 }}>
                            Membres ({detail.data.members.length})
                          </div>
                          <table className="table" style={{ fontSize: 12 }}>
                            <tbody>
                              {detail.data.members.map((m) => (
                                <tr key={m.id}>
                                  <td>
                                    {m.name}
                                    {m.isInitiator ? ' · initiateur' : ''}
                                  </td>
                                  <td className="right">×{m.quantity}</td>
                                  <td>{MEMBER_STATUS_FR[m.status] ?? m.status}</td>
                                  <td className="muted">
                                    {m.orderId ? `commande liée` : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div>
                          <div className="eyebrow" style={{ marginBottom: 6 }}>
                            Journal
                          </div>
                          <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 12 }}>
                            {detail.data.journal.slice(0, 12).map((e, i) => (
                              <li key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                <span>
                                  {EVENT_FR[e.type] ?? e.type}{' '}
                                  <span className="muted">
                                    ({ACTOR_FR[e.actorType] ?? e.actorType})
                                  </span>
                                </span>
                                <span className="muted">
                                  {new Date(e.createdAt).toLocaleString('fr-FR', {
                                    day: '2-digit',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {!list.isLoading && (list.data?.items ?? []).length === 0 && (
              <tr>
                <td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                  Aucune campagne{status ? ' pour ce statut' : ''}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
